import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Mail, MapPin, Send, MessageCircle, Clock, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { useData } from '../context/DataContext';

export const Contact = () => {
  const { submitContact } = useData();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });

  const whatsappNumber = "56962942789";
  const whatsappMessage = encodeURIComponent("Hola! Me interesa conocer mas sobre sus servicios de IA y automatizacion.");

  const serviceOptions = [
    { value: 'ia', label: 'Inteligencia Artificial' },
    { value: 'analytics', label: 'Analisis de Datos' },
    { value: 'automation', label: 'Automatizacion de Procesos' },
    { value: 'software', label: 'Software a Medida' },
    { value: 'other', label: 'Otro / No estoy seguro' }
  ];

  const openWhatsApp = () => {
    window.open(`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`, '_blank');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, service: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const selectedService = serviceOptions.find(s => s.value === formData.service)?.label || 'No especificado';

    try {
      const result = await submitContact({
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        company: formData.company || null,
        message: formData.message,
        subject: `Consulta sobre ${selectedService} - ${formData.company || 'Cliente Potencial'}`,
        status: 'pending'
      });

      if (result.success) {
        toast.success('Recibimos tu mensaje. Te contactaremos en menos de 2 horas.');
        setFormData({ name: '', email: '', phone: '', company: '', service: '', message: '' });
      } else {
        toast.error('Error al enviar el mensaje. Por favor, intenta de nuevo.');
      }
    } catch (error) {
      toast.error('Error al enviar el mensaje. Por favor, intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contacto" className="py-24 lg:py-32 bg-background relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-sm font-medium text-accent uppercase tracking-[0.2em] mb-4 block">
            Contacto
          </span>
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-foreground leading-tight mb-4 text-balance">
            Hablemos de tu proyecto
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto leading-relaxed mb-6">
            Contactanos para una consulta gratuita y descubre como podemos ayudarte.
          </p>
          <div className="inline-flex items-center gap-2 bg-accent/10 border border-accent/20 rounded-full px-5 py-2.5">
            <Clock className="h-4 w-4 text-accent" />
            <span className="text-sm font-medium text-foreground">Respuesta garantizada en menos de 2 horas</span>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
          {/* Form */}
          <div className="lg:col-span-2">
            <div className="bg-card border border-border rounded-2xl p-8 lg:p-10">
              <h3 className="font-display text-2xl font-bold text-foreground mb-8">Envianos un Mensaje</h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Service Selection */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">
                    Que servicio te interesa?
                  </label>
                  <Select value={formData.service} onValueChange={handleServiceChange}>
                    <SelectTrigger className="border-border bg-background focus:border-accent focus:ring-accent rounded-xl h-12">
                      <SelectValue placeholder="Selecciona un servicio" />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label htmlFor="name" className="text-sm font-medium text-foreground">Nombre *</label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="border-border bg-background focus:border-accent focus:ring-accent rounded-xl h-12"
                      placeholder="Tu nombre completo"
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="email" className="text-sm font-medium text-foreground">Email *</label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="border-border bg-background focus:border-accent focus:ring-accent rounded-xl h-12"
                      placeholder="tu@email.com"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label htmlFor="phone" className="text-sm font-medium text-foreground">Telefono</label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="border-border bg-background focus:border-accent focus:ring-accent rounded-xl h-12"
                      placeholder="+56 9 1234 5678"
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="company" className="text-sm font-medium text-foreground">Empresa</label>
                    <Input
                      id="company"
                      name="company"
                      type="text"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="border-border bg-background focus:border-accent focus:ring-accent rounded-xl h-12"
                      placeholder="Nombre de tu empresa"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="message" className="text-sm font-medium text-foreground">Mensaje *</label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    value={formData.message}
                    onChange={handleInputChange}
                    className="border-border bg-background focus:border-accent focus:ring-accent rounded-xl"
                    placeholder="Cuentanos sobre tu proyecto y como podemos ayudarte..."
                  />
                </div>

                <Button
                  type="submit"
                  size="lg"
                  disabled={loading}
                  className="w-full bg-foreground text-background hover:bg-foreground/90 rounded-full py-6 text-base font-medium transition-all duration-300 group disabled:opacity-50"
                >
                  {loading ? 'Enviando...' : 'Enviar y Recibir Propuesta en 24h'}
                  <Send className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
                </Button>
              </form>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* WhatsApp Card */}
            <div
              onClick={openWhatsApp}
              className="bg-foreground text-background rounded-2xl p-8 cursor-pointer hover:bg-foreground/90 transition-all duration-300 group"
            >
              <div className="w-12 h-12 rounded-xl bg-background/10 flex items-center justify-center mb-6">
                <MessageCircle className="h-6 w-6 text-background" />
              </div>
              <h3 className="font-bold text-lg text-background mb-1">WhatsApp</h3>
              <p className="text-background/80 font-medium mb-1">+56 9 6294 2789</p>
              <p className="text-background/50 text-sm mb-4">Respuesta inmediata</p>
              <div className="flex items-center gap-2 text-accent text-sm font-medium">
                <span>Chatear ahora</span>
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
              </div>
            </div>

            {/* Email Card */}
            <div className="bg-card border border-border rounded-2xl p-8">
              <div className="w-12 h-12 rounded-xl bg-foreground/5 flex items-center justify-center mb-6">
                <Mail className="h-6 w-6 text-foreground" />
              </div>
              <h3 className="font-bold text-lg text-foreground mb-1">Email</h3>
              <p className="text-accent font-medium mb-1">hola@neurolytics.cl</p>
              <p className="text-muted-foreground text-sm">Respuesta en 24h</p>
            </div>

            {/* Location Card */}
            <div className="bg-card border border-border rounded-2xl p-8">
              <div className="w-12 h-12 rounded-xl bg-foreground/5 flex items-center justify-center mb-6">
                <MapPin className="h-6 w-6 text-foreground" />
              </div>
              <h3 className="font-bold text-lg text-foreground mb-1">Ubicacion</h3>
              <p className="text-muted-foreground text-sm">Los Lagos, Chile</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
